import * as React from 'react'

import useI18n from '@store/i18n/useI18n'
import apis from '@app/api/api'
import * as AccountStore from '@store/account/account'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'
import { motion, container, item } from '@utils/Motion'
import useDebounce from '@utils/useDebounce'

import Loader from 'react-loader-spinner'
import Images from '@components/images/images'
import Button from '@components/button/button'
import Select from '@components/select/select'
import Swal from 'sweetalert2'
import BusinessesSelect from '@components/select/businessesSelect'

import { FiChevronRight, FiChevronDown } from 'react-icons/fi'

import { BsDownload } from 'react-icons/bs'
import DownloadingComponent from '@utils/ImageDownloader'
import rights from '@utils/RightManager'

interface Props {
  navigation: Navigation
  screenStatus: ScreenStatus
  orphan: OrphanDeclarationDetails | undefined
  steps: BusinessStep[]
}

const OrphanFocusMenu = ({ navigation, screenStatus, orphan, steps }: Props) => {
  const i18n = useI18n()

  const [buttonState, setButtonState] = React.useState<'affecting' | 'deleting' | undefined>()
  const [downloading, setDownloading] = React.useState<boolean>(false)

  const account = AccountStore.store.getState().account
  const regionsOptions: Option[] | undefined = account && account.regions
  const [selectedRegion, setSelectedRegion] = React.useState<Option | undefined>(undefined)

  const [businessStatus, setBusinessStatus] = React.useState<ScreenStatus>('fetched')
  const [businesses, setBusinesses] = React.useState<BusinessResume[]>([])
  const [selectedBusiness, setSelectedBusiness] = React.useState<BusinessResume | undefined>(undefined)

  const [text, bounced, setText] = useDebounce('')

  const [selectedStep, setSelectedStep] = React.useState<Option | undefined>(undefined)
  const [selectedTheme, setSelectedTheme] = React.useState<Option | undefined>(undefined)
  const [themesOptions, setThemesOptions] = React.useState<Option[]>([])

  React.useEffect(() => {
    const initialStep: BusinessStep | undefined =
      orphan && orphan.description.idStep ? steps.find((s) => s.id === orphan.description.idStep) : undefined
    const initialStepOption: Option | undefined = initialStep && { value: initialStep.id, label: initialStep.wording }
    const initialTheme: BusinessTheme | undefined =
      orphan && initialStep && orphan.description.idTheme
        ? initialStep.themes.find((t) => t.id === orphan.description.idTheme)
        : undefined
    const initialThemeOption: Option | undefined = initialTheme && {
      value: initialTheme.id,
      label: initialTheme.wording,
    }
    const initialThemes: BusinessTheme[] = initialStep ? initialStep.themes : []
    const initialThemesOptions: Option[] = initialThemes.map((t) => ({ value: t.id, label: t.wording }))
    !!initialStepOption && setSelectedStep(initialStepOption)
    !!initialThemeOption && setSelectedTheme(initialThemeOption)
    setThemesOptions(initialThemesOptions)
  }, [orphan, steps])

  const handleStepChanged = (newStep?: Option) => {
    const sstep = newStep && steps.find((s) => s.id === newStep.value)
    const stheme = sstep && selectedTheme && sstep.themes.find((t) => t.id === selectedTheme.value)
    const themes = sstep ? sstep.themes : []

    setSelectedStep(newStep)
    setSelectedTheme(stheme && { value: stheme.id, label: stheme.wording })
    setThemesOptions(themes.map((t) => ({ value: t.id, label: t.wording })))
  }

  React.useEffect(() => {
    setSelectedBusiness(undefined)
  }, [selectedRegion])

  const fetchBusiness = (regionId: string, bounced: string) => {
    setBusinessStatus('loading')
    apis.business
      .list({
        stepIds: [],
        regions: [regionId],
        text: bounced,
        hasDeclarations: [],
      })
      .then((blist) => {
        setBusinesses(blist)
        setBusinessStatus('fetched')
      })
      .catch(() => setBusinessStatus('error'))
  }

  React.useEffect(() => {
    if (selectedRegion) {
      fetchBusiness(selectedRegion.value, bounced)
    }
  }, [selectedRegion, bounced])

  const deleteOrphanDeclaration = (orphanId: string) => {
    setButtonState('deleting')
    Swal.fire({
      title: i18n.t('components.detailsOrphan.delete.confirm.title'),
      text: i18n.t('components.detailsOrphan.delete.confirm.text'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3A8641',
      cancelButtonColor: '#0053A1',
      confirmButtonText: i18n.t('components.detailsOrphan.delete.confirm.confirm'),
      cancelButtonText: i18n.t('components.detailsOrphan.delete.confirm.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        apis.orphan
          .delete(orphanId)
          .then(() => {
            Swal.fire(
              i18n.t('components.detailsOrphan.delete.success.title'),
              i18n.t('components.detailsOrphan.delete.success.text'),
              'success'
            ).then(() => {
              setButtonState(undefined)
              navigation.goBack()
            })
          })
          .catch(() => {
            Swal.fire(
              i18n.t('components.detailsOrphan.delete.failure.title'),
              i18n.t('components.detailsOrphan.delete.failure.text'),
              'error'
            ).then(() => setButtonState(undefined))
          })
      } else {
        setButtonState(undefined)
      }
    })
  }

  const affectOrphanDeclaration = (orphanId: string) => {
    if (!!selectedBusiness && !!selectedStep && !!selectedTheme) {
      setButtonState('affecting')
      Swal.fire({
        title: i18n.t('components.detailsOrphan.affect.confirm.title'),
        text: i18n.t('components.detailsOrphan.affect.confirm.text'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3A8641',
        cancelButtonColor: '#0053A1',
        confirmButtonText: i18n.t('components.detailsOrphan.affect.confirm.confirm'),
        cancelButtonText: i18n.t('components.detailsOrphan.affect.confirm.cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          apis.orphan
            .affect(orphanId, selectedBusiness.id, selectedTheme.value)
            .then((declaration) => {
              Swal.fire(
                i18n.t('components.detailsOrphan.affect.success.title'),
                i18n.t('components.detailsOrphan.affect.success.text'),
                'success'
              ).then(() => {
                setButtonState(undefined)
                navigation.replace(`/focus/business/${declaration.location.idBusiness}`)
              })
            })
            .catch(() => {
              Swal.fire(
                i18n.t('components.detailsOrphan.affect.failure.title'),
                i18n.t('components.detailsOrphan.affect.failure.text'),
                'error'
              ).then(() => setButtonState(undefined))
            })
        } else {
          setButtonState(undefined)
        }
      })
    }
  }

  return (
    <Container>
      <ScreenTitleContainer>
        <ScreenTitle>{i18n.t('components.detailsOrphan.title')}</ScreenTitle>
        <Back onClick={navigation.goBack}>{i18n.t('components.menu.back')}</Back>
      </ScreenTitleContainer>
      {screenStatus === 'loading' ? (
        <StatusContainer>
          <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={30} />
        </StatusContainer>
      ) : screenStatus === 'error' || !orphan ? (
        <ErrorLibelle>{i18n.t('components.detailsChantier.error')}</ErrorLibelle>
      ) : (
        <DataContainer>
          {selectedStep && selectedTheme && (
            <ListTitleContainer>
              <ListTitle>
                <ListTitleLibelle>{selectedStep.label}</ListTitleLibelle>
                <FiChevronRight size={15} />
              </ListTitle>
              <ListTitle>
                <ListTitleLibelle>
                  {i18n.t('components.detailsChantier.step', {
                    step: selectedTheme.label,
                    count: orphan.pictures.length,
                  })}
                </ListTitleLibelle>
                <FiChevronDown size={15} />
              </ListTitle>
            </ListTitleContainer>
          )}
          <ImagesContainer
            as={motion.div}
            className="container"
            variants={container}
            initial="hidden"
            animate="visible">
            {orphan.pictures.map((picture, idx) => (
              <motion.div key={picture.id} className="item" variants={idx > 20 ? undefined : item}>
                <Images
                  key={picture.id}
                  imageId={picture.id}
                  index={idx}
                  images={orphan.pictures}
                  size={195}
                  specialhover={false}
                  status={picture.status}
                />
              </motion.div>
            ))}
          </ImagesContainer>
          <InformationContainer>
            <TitleContainer>
              <Title>{i18n.t('components.detailsOrphan.information')}</Title>
              {orphan.pictures.length !== 0 &&
                (!downloading ? (
                  <DownloadAction onClick={() => setDownloading(true)}>
                    <BsDownload size={30} color={Theme.colors.filterPrimarycolor} />
                  </DownloadAction>
                ) : (
                  <DownloadAction>
                    <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={10} />
                  </DownloadAction>
                ))}
            </TitleContainer>
            <Info>
              {i18n.t('components.detailsOrphan.subtitle', {
                firstName: orphan.description.firstname,
                lastName: orphan.description.lastname,
                date: new Date(orphan.description.date),
              })}
            </Info>
            {orphan.description.location && (
              <>
                <SubTitleContainer>
                  <SubTitle>{i18n.t('components.detailsOrphan.localisation')}</SubTitle>
                </SubTitleContainer>
                <Info>{orphan.description.location}</Info>
              </>
            )}
            {orphan.description.comment && (
              <>
                <SubTitleContainer>
                  <SubTitle>{i18n.t('components.detailsOrphan.commentaire')}</SubTitle>
                </SubTitleContainer>
                <Info>{orphan.description.comment}</Info>
              </>
            )}
            {rights.canAffectOrphan() ? (
              <>
                <SelectorContainer>
                  <SubTitleContainer>
                    <SubTitle>{i18n.t('components.detailsOrphan.business')}</SubTitle>
                  </SubTitleContainer>
                  {regionsOptions && (
                    <Select
                      options={regionsOptions}
                      selected={selectedRegion}
                      setSelected={setSelectedRegion}
                      placeholder={i18n.t('components.detailsOrphan.placeholder.region')}
                      bordered={!selectedRegion}
                    />
                  )}
                  <BusinessesSelect
                    businesses={businesses}
                    selected={selectedBusiness}
                    setSelected={setSelectedBusiness}
                    placeholder={i18n.t('components.detailsOrphan.placeholder.business')}
                    businessStatus={businessStatus}
                    text={text}
                    setText={setText}
                    isDisabled={!selectedRegion}
                    bordered={!selectedBusiness}
                    isClearable={true}
                  />
                </SelectorContainer>
                <SelectorContainer>
                  <SubTitleContainer>
                    <SubTitle>{i18n.t('components.detailsOrphan.steps')}</SubTitle>
                  </SubTitleContainer>
                  <Select
                    options={steps.map((s) => ({ value: s.id, label: s.wording }))}
                    selected={selectedStep}
                    setSelected={handleStepChanged}
                    placeholder={i18n.t('components.detailsOrphan.placeholder.step')}
                    bordered={!selectedStep}
                  />
                  <Select
                    options={themesOptions}
                    selected={selectedTheme}
                    setSelected={setSelectedTheme}
                    placeholder={i18n.t('components.detailsOrphan.placeholder.theme')}
                    bordered={!selectedTheme}
                  />
                </SelectorContainer>
              </>
            ) : (
              <>
                <SubTitleContainer>
                  <SubTitle>{i18n.t('components.detailsOrphan.stepSelected')}</SubTitle>
                </SubTitleContainer>
                <Info>{selectedStep ? selectedStep.label : i18n.t('components.detailsOrphan.noStepSelected')}</Info>

                <SubTitleContainer>
                  <SubTitle>{i18n.t('components.detailsOrphan.themeSelected')}</SubTitle>
                </SubTitleContainer>
                <Info>{selectedTheme ? selectedTheme.label : i18n.t('components.detailsOrphan.noThemeSelected')}</Info>
              </>
            )}
          </InformationContainer>
          {rights.canAffectOrphan() && orphan && (
            <BottomContainer>
              <Button
                type="action"
                title={i18n.t('components.detailsOrphan.buttons.delete')}
                onClick={() => deleteOrphanDeclaration(orphan.id)}
                status={!buttonState ? 'active' : buttonState === 'deleting' ? 'loading' : 'disabled'}
              />
              <Button
                type="navigation"
                title={i18n.t('components.detailsOrphan.buttons.affect')}
                onClick={() => affectOrphanDeclaration(orphan.id)}
                status={
                  selectedBusiness && selectedStep && selectedTheme && !buttonState
                    ? 'active'
                    : buttonState === 'affecting'
                    ? 'loading'
                    : 'disabled'
                }
              />
            </BottomContainer>
          )}
          {downloading && (
            <DownloadingComponent
              pictures={orphan.pictures.map((p) => ({ id: p.id, path: '', name: `Photo_orpheline` }))}
              cancel={() => setDownloading(false)}
            />
          )}
        </DataContainer>
      )}
    </Container>
  )
}

export default OrphanFocusMenu

const Container = styled('div')`
  flex: 1;
  display: flex;
  overflow: hidden;
  margin-bottom: 10px;
  flex-direction: column;
  padding: 10px;

  .swal2-title {
    font-size: 1.6rem !important;
    font-family: Avenir, -webkit-pictograph, sans-serif;
  }
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
  padding: 15px 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const ListTitleContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 5px 6px;
`
const ListTitle = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`
const ListTitleLibelle = styled('div')`
  ${(props) => props.theme.fonts.chantierMenuLibelle};
  text-decoration-line: underline;
  flex: none;
  order: 0;
  flex-grow: 0;
`
const DataContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: scroll;
`
const ImagesContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  padding-top: 10px;
`
const BottomContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-direction: column;
  min-height: 150px;
  margin: 15px 0 200px 0;
`
const InformationContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
`
const TitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
  padding: 15px 0;
  margin: 10px 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const Title = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const SubTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
  padding: 5px 0;
  margin: 10px 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SubTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
  font-size: 15px;
`
const Info = styled('div')`
  ${(props) => props.theme.fonts.chantierMenuSubtitle};
`
const SelectorContainer = styled('div')`
  display: flex;
  flex-direction: column;
`
const DownloadAction = styled('div')`
  padding: 5px;
  cursor: pointer;
  height: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
`
